import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  CurrencyRupeeOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  PendingActionsOutlined,
  ReceiptLongOutlined,
  RefreshOutlined,
  SearchOutlined,
  TaskAltOutlined,
  VisibilityOutlined,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import backgroundPattern from '../../assets/pasbookpng.png';
import '../Invoices/Invoice.css';

const STORAGE_KEY = 'debitNotes';
const CREATE_PATH = '/dashboard/debit-notes/new';
const STATUS_FILTERS = ['All', 'Draft', 'Open', 'Paid', 'Void'];

const money = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getStatus = (note) => {
  const value = String(note?.status || 'Draft').trim().toLowerCase();
  if (value === 'draft') return 'Draft';
  if (value === 'open') return 'Open';
  if (value === 'paid') return 'Paid';
  if (value === 'void' || value === 'cancelled' || value === 'canceled') return 'Void';
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Draft';
};

const noteNumber = (note) =>
  note?.debitNoteNo || note?.noteNo || `DN-${String(note?.id || '').padStart(5, '0')}`;

const customerName = (note) =>
  note?.customer || note?.customerName || note?.customer?.name || '-';

const customerPhone = (note) =>
  note?.phone || note?.customerPhone || note?.customer?.phone || '';

const readNotes = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to read debit notes:', error);
    return [];
  }
};

const Status = ({ note }) => {
  const status = getStatus(note);
  const className = status === 'Open' ? 'sent' : status === 'Void' ? 'overdue' : status.toLowerCase().replace(/\s+/g, '-');
  return (
    <Box className={`invoice-status invoice-status-${className}`}>
      <span />
      {status}
    </Box>
  );
};

export default function DebitNoteList() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const loadNotes = () => {
    setLoading(true);
    try {
      setNotes(readNotes());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
    const onStorage = () => setNotes(readNotes());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const stats = useMemo(() => {
    const totalValue = notes.reduce((sum, note) => sum + Number(note.total ?? note.totalAmount ?? 0), 0);
    const paid = notes.filter((note) => getStatus(note) === 'Paid')
      .reduce((sum, note) => sum + Number(note.total ?? note.totalAmount ?? 0), 0);
    const open = notes.filter((note) => getStatus(note) === 'Open').length;
    return {
      count: notes.length,
      value: totalValue,
      paid,
      outstanding: Math.max(totalValue - paid, 0),
      open,
    };
  }, [notes]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return [...notes]
      .filter((note) => {
        const searchable = [
          noteNumber(note),
          customerName(note),
          note?.refNo,
          note?.reference,
          note?.subject,
        ].filter(Boolean).join(' ').toLowerCase();
        const matchesSearch = !term || searchable.includes(term);
        const matchesStatus = filter === 'All' || getStatus(note) === filter;
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  }, [notes, search, filter]);

  const removeNote = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const next = notes.filter((note) => note.id !== deleteTarget.id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setNotes(next);
      setDeleteTarget(null);
      setSelected(null);
      toast.success('Debit note deleted successfully.');
    } catch (error) {
      console.error('Debit note delete failed:', error);
      toast.error('Unable to delete debit note.');
    } finally {
      setDeleting(false);
    }
  };

  const editNote = (note) => {
    navigate(CREATE_PATH, { state: { editData: note, noteType: 'Debit' } });
  };

  return (
    <Box className="invoice-page invoice-list-page">
      <div className="invoice-background-watermark" style={{ backgroundImage: `url(${backgroundPattern})` }} />

      <Box className="invoice-shell">
        <Box className="invoice-page-header">
          <Box className="invoice-page-header-left invoice-header-title-group">
            <Box className="invoice-page-icon"><ReceiptLongOutlined /></Box>
            <Box className="invoice-page-title-wrap">
              <Typography className="invoice-page-title">Debit Notes</Typography>
              <Typography className="invoice-page-subtitle">Manage Manage supplier debits, purchase adjustments and issued notes.</Typography>
            </Box>
          </Box>
          <Button className="invoice-header-create-btn" startIcon={<Add />} onClick={() => navigate(CREATE_PATH)}>
            New Debit Note
          </Button>
        </Box>

        <Box className="invoice-list-summary">
          <Card className="invoice-summary-card invoice-summary-card-total">
            <Box className="invoice-summary-icon"><ReceiptLongOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Total debit notes</span>
              <strong>{stats.count}</strong>
              <small>All debit notes</small>
            </Box>
          </Card>
          <Card className="invoice-summary-card invoice-summary-card-value">
            <Box className="invoice-summary-icon"><CurrencyRupeeOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Total value</span>
              <strong>{money(stats.value)}</strong>
              <small>Debit amount</small>
            </Box>
          </Card>
          <Card className="invoice-summary-card invoice-summary-card-paid">
            <Box className="invoice-summary-icon"><TaskAltOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Paid</span>
              <strong>{money(stats.paid)}</strong>
              <small>Settled amount</small>
            </Box>
          </Card>
          <Card className="invoice-summary-card invoice-summary-card-outstanding">
            <Box className="invoice-summary-icon"><PendingActionsOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Outstanding</span>
              <strong>{money(stats.outstanding)}</strong>
              <small>{stats.open} open note{stats.open === 1 ? '' : 's'}</small>
            </Box>
          </Card>
        </Box>

        {!filtersOpen ? (
          <button
            type="button"
            className="invoice-show-filters-btn"
            onClick={() => setFiltersOpen(true)}
          >
            <SearchOutlined className="invoice-show-filters-icon" />
            <span>Show Filters</span>
          </button>
        ) : (
          <Card className="invoice-filter-card">
            <CardContent>
              <Box className="invoice-filter-head">
                <Box className="invoice-filter-head-left">
                  <button
                    type="button"
                    className="invoice-filter-title-btn"
                    onClick={() => setFiltersOpen(false)}
                    aria-label="Hide filters"
                    title="Hide filters"
                  >
                    <Box className="invoice-filter-icon"><SearchOutlined /></Box>
                    <Typography className="invoice-filter-title">Filters</Typography>
                    <ExpandLessIcon className="invoice-filter-collapse-icon" />
                  </button>
                </Box>
                <Button
                  className="invoice-filter-clear"
                  onClick={() => { setSearch(''); setFilter('All'); }}
                  disabled={!search && filter === 'All'}
                >
                  Clear
                </Button>
              </Box>
              <Stack className="invoice-filter-controls" direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField
                  className="invoice-field invoice-search-field"
                  placeholder="Search debit note number, customer or reference"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <SearchOutlined sx={{ color: '#94a3b8', mr: 1 }} /> }}
                />
                <TextField select className="invoice-field invoice-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
                  {STATUS_FILTERS.map((item) => (
                    <MenuItem key={item} value={item}>{item === 'All' ? 'All statuses' : item}</MenuItem>
                  ))}
                </TextField>
                <Typography className="invoice-results-count">
                  {filtered.length} note{filtered.length === 1 ? '' : 's'}
                </Typography>
                <IconButton
                  className="invoice-row-action"
                  onClick={(event) => {
                    loadNotes();

                    const icon = event.currentTarget.querySelector('.debit-refresh-icon');
                    if (!icon || typeof icon.animate !== 'function') return;

                    icon.getAnimations().forEach((animation) => animation.cancel());

                    const animation = icon.animate(
                      [
                        { transform: 'rotate(0deg)' },
                        { transform: 'rotate(360deg)' },
                      ],
                      {
                        duration: 1000,
                        iterations: 1,
                        easing: 'linear',
                        fill: 'none',
                      }
                    );

                    animation.onfinish = () => {
                      icon.style.transform = '';
                    };
                  }}
                  title="Refresh"
                  aria-label="Refresh debit notes"
                >
                  <RefreshOutlined className="debit-refresh-icon" />
                </IconButton>
            </Stack>
          </CardContent>
        </Card>
        )}

        <Card className="invoice-list-card">
          {loading ? (
            <Box className="invoice-loading"><CircularProgress size={28} /><Typography>Loading debit notes…</Typography></Box>
          ) : filtered.length === 0 ? (
            <Box className="invoice-empty">
              <Box className="invoice-empty-icon"><ReceiptLongOutlined /></Box>
              <Typography>No debit notes found</Typography>
              <span>{notes.length ? 'Try changing your search or status filter.' : 'Create your first debit note to see it here.'}</span>
              {!notes.length && (
                <Button className="invoice-primary-btn" startIcon={<Add />} onClick={() => navigate(CREATE_PATH)}>
                  Create Debit Note
                </Button>
              )}
            </Box>
          ) : (
            <Box className="invoice-table-container">
              <Table className="invoice-professional-table invoice-responsive-list-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Debit Note #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Note date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((note) => (
                    <TableRow key={note.id || noteNumber(note)} hover>
                      <TableCell data-label="Debit Note #">
                        <button type="button" className="invoice-number-link" onClick={() => setSelected(note)}>
                          {noteNumber(note)}
                        </button>
                      </TableCell>
                      <TableCell data-label="Customer">
                        <Box>
                          <Typography className="invoice-customer-name">{customerName(note)}</Typography>
                          {customerPhone(note) && <Typography className="invoice-customer-phone">{customerPhone(note)}</Typography>}
                        </Box>
                      </TableCell>
                      <TableCell data-label="Reference">{note.refNo || note.reference || '-'}</TableCell>
                      <TableCell data-label="Note date">{formatDate(note.date)}</TableCell>
                      <TableCell data-label="Status"><Status note={note} /></TableCell>
                      <TableCell data-label="Total" align="right"><Typography className="invoice-amount">{money(note.total ?? note.totalAmount)}</Typography></TableCell>
                      <TableCell data-label="Actions">
                        <Box className="invoice-row-actions">
                          <IconButton className="invoice-row-action invoice-row-view" title="View" onClick={() => setSelected(note)}>
                            <VisibilityOutlined />
                          </IconButton>
                          <IconButton className="invoice-row-action invoice-row-edit" title="Edit" onClick={() => editNote(note)}>
                            <EditOutlined />
                          </IconButton>
                          <IconButton className="invoice-row-action invoice-row-delete" title="Delete" onClick={() => setDeleteTarget(note)}>
                            <DeleteOutlineOutlined />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Card>
      </Box>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>{selected ? `Debit Note ${noteNumber(selected)}` : 'Debit Note'}</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                <Box flex={1}><Typography variant="caption">Customer</Typography><Typography fontWeight={700}>{customerName(selected)}</Typography></Box>
                <Box flex={1}><Typography variant="caption">Date</Typography><Typography fontWeight={700}>{formatDate(selected.date)}</Typography></Box>
                <Box flex={1}><Typography variant="caption">Reference</Typography><Typography fontWeight={700}>{selected.refNo || selected.reference || '-'}</Typography></Box>
                <Box flex={1}><Typography variant="caption">Total</Typography><Typography fontWeight={800}>{money(selected.total ?? selected.totalAmount)}</Typography></Box>
              </Stack>
              <Typography variant="subtitle2">Items</Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell>Item</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Rate</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                <TableBody>
                  {(selected.items || []).map((item, index) => {
                    const qty = Number(item.qty ?? item.quantity ?? 0);
                    const rate = Number(item.rate ?? item.price ?? 0);
                    const discount = Number(item.disc ?? item.discount ?? 0);
                    return (
                      <TableRow key={item.id || index}>
                        <TableCell>{item.item || item.name || item.description || '-'}</TableCell>
                        <TableCell align="right">{qty}</TableCell>
                        <TableCell align="right">{money(rate)}</TableCell>
                        <TableCell align="right">{money(qty * rate - discount)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
          {selected && <Button variant="contained" onClick={() => { setSelected(null); editNote(selected); }}>Edit Debit Note</Button>}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Debit Note?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">This will permanently remove {deleteTarget ? noteNumber(deleteTarget) : 'this debit note'} from the saved notes.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={removeNote} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
